<template>
  <ImageViewDialog 
    v-if="showImageView"
    :images="viewerImages" 
    :startIndex="viewerStartIndex"
    :oth_showTodoEdit="closeImageView"
  />
  
  <div class="dialog-overlay" @click.self="handleClose">
    <div class="dialog-container">
      <!-- 컴팩트 헤더 -->
      <div class="view-header">
        <div class="header-left">
          <img 
            v-if="policy?.short_img" 
            :src="policy.short_img" 
            :alt="policy.company_name"
            class="company-logo"
          />
          <div v-else class="company-logo placeholder">
            {{ policy?.company_name?.charAt(0) || '?' }}
          </div>
          <span class="company-name">{{ policy?.company_name }}</span>
        </div>
        <div class="header-right">
          <span class="status-pill" :class="statusClass">
            {{ statusText }}
          </span>
          <button class="btn-close" @click="handleClose">
            <i class="ph ph-x"></i>
          </button>
        </div>
      </div>

      <!-- 스크롤 콘텐츠 -->
      <div class="view-body">
        <!-- 시책 포스터 -->
        <div 
          v-if="policy?.image" 
          class="poster-image"
          @click="openPosterView"
        >
          <img :src="getFullUrl(policy.image)" alt="시책 포스터" />
          <div class="poster-zoom">
            <i class="ph ph-magnifying-glass-plus"></i>
          </div>
        </div>

        <!-- 제목 + 기간 -->
        <div class="title-section">
          <h2 class="policy-title">{{ policy?.title }}</h2>
          <span class="date-range">
            <i class="ph ph-calendar-blank"></i>
            {{ formatDateRange(policy?.start_date, policy?.expire_date) }}
          </span>
        </div>

        <!-- 설명 -->
        <p v-if="policy?.description" class="description">{{ policy.description }}</p>

        <!-- 핵심 지표 -->
        <div class="stats-bar">
          <div class="stat">
            <span class="stat-num">{{ formatNumber(maxAmount) }}<small>%</small></span>
            <span class="stat-label">최대 시책</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat">
            <span class="stat-num">{{ periodCount }}<small>구간</small></span>
            <span class="stat-label">시책 기간</span>
          </div>
          <div class="stat-divider"></div>
          <div class="stat">
            <span class="stat-num">{{ calculateDays }}<small>일</small></span>
            <span class="stat-label">총 일수</span>
          </div>
        </div>

        <!-- 구간별 시책 (주차가 있을 때) -->
        <template v-if="weeksList.length > 0">
          <div class="section-label">구간별 시책</div>
          
          <div class="timeline">
            <div 
              v-for="(week, index) in weeksList" 
              :key="index"
              class="timeline-item"
              :class="getWeekStatus(week)"
            >
              <!-- 타임라인 도트 & 라인 -->
              <div class="timeline-rail">
                <div class="dot"></div>
                <div v-if="index < weeksList.length - 1" class="line"></div>
              </div>

              <!-- 콘텐츠 -->
              <div class="timeline-content">
                <!-- 라벨 + 상태 -->
                <div class="period-head">
                  <span class="period-label">{{ getWeekLabel(week) }}</span>
                  <span class="period-status" :class="getWeekStatus(week)">
                    <template v-if="getWeekStatus(week) === 'active'">
                      진행중 · {{ getWeekRemainingDays(week) }}일 남음
                    </template>
                    <template v-else-if="getWeekStatus(week) === 'upcoming'">
                      {{ getWeekDaysUntilStart(week) }}일 후 시작
                    </template>
                    <template v-else>종료</template>
                  </span>
                </div>

                <!-- 날짜 -->
                <span class="period-dates">
                  {{ formatDateShort(week.start_date) }} ~ {{ formatDateShort(week.end_date) }}
                </span>

                <!-- 진행바 (진행중일 때만) -->
                <div v-if="getWeekStatus(week) === 'active'" class="mini-progress">
                  <div class="mini-track">
                    <div class="mini-fill" :style="{ width: getWeekProgress(week) + '%' }"></div>
                  </div>
                  <span class="mini-percent">{{ Math.round(getWeekProgress(week)) }}%</span>
                </div>

                <!-- 금액 + 목표 -->
                <div class="period-detail">
                  <span class="period-amount">{{ formatNumber(week.amount) }}%</span>
                  <span v-if="week.goal" class="period-goal">{{ week.goal }}</span>
                </div>

                <!-- 이미지들 -->
                <div v-if="getPeriodImages(week).length > 0" class="period-images">
                  <div 
                    v-for="(img, imgIdx) in getPeriodImages(week)" 
                    :key="imgIdx"
                    class="period-image"
                    @click="openPeriodImageView(index, imgIdx)"
                  >
                    <img :src="getFullUrl(img)" alt="구간 이미지" />
                    <div class="image-zoom-sm">
                      <i class="ph ph-magnifying-glass-plus"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- 단일 시책 (주차 없을 때) -->
        <template v-else>
          <!-- 진행 바 -->
          <div class="progress-section">
            <div class="progress-endpoints">
              <span>{{ formatDateShort(policy?.start_date) }}</span>
              <span v-if="statusClass === 'active'" class="remaining">
                {{ remainingDays }}일 남음
              </span>
              <span>{{ formatDateShort(policy?.expire_date) }}</span>
            </div>
            <div class="progress-track">
              <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
            </div>
          </div>

          <!-- 목표 -->
          <div v-if="policy?.goal" class="goal-block">
            <div class="goal-header">
              <i class="ph-fill ph-target"></i>
              <span>목표</span>
            </div>
            <p>{{ policy.goal }}</p>
          </div>
        </template>

        <!-- 상세 이미지 그리드 -->
        <div v-if="extraImagesList.length > 0" class="extra-images">
          <div class="section-label">
            상세 이미지
            <span class="count">{{ extraImagesList.length }}</span>
          </div>
          <div class="image-grid">
            <div 
              v-for="(img, index) in extraImagesList" 
              :key="index"
              class="grid-image"
              @click="openImageView('extra', index)"
            >
              <img :src="img" :alt="`이미지 ${index + 1}`" />
            </div>
          </div>
        </div>

        <!-- 메타 -->
        <div class="meta">
          <span><i class="ph ph-user"></i> {{ policy?.mb_id }}</span>
          <span><i class="ph ph-clock"></i> {{ formatDateTime(policy?.created_at) }}</span>
        </div>
      </div>

      <!-- 액션 -->
      <div class="view-footer">
        <button class="btn-ghost-danger" @click="handleDelete">
          <i class="ph ph-trash"></i>
          삭제
        </button>
        <button class="btn-primary" @click="handleEdit">
          <i class="ph ph-pencil-simple"></i>
          수정
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue'
import ImageViewDialog from '@/components/dialogs/imageView.vue'

const props = defineProps({
  policy: { type: Object, default: null }
})

const emit = defineEmits(['close', 'edit', 'delete'])

const showImageView = ref(false)
const viewerImages = ref([])
const viewerStartIndex = ref(0)

// ── weeks 파싱 ──
const weeksList = computed(() => {
  if (!props.policy?.weeks) return []
  try {
    const parsed = typeof props.policy.weeks === 'string' 
      ? JSON.parse(props.policy.weeks) 
      : props.policy.weeks
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
})

// ── 라벨 표시 (자유 라벨 우선, 없으면 N주차 폴백) ──
function getWeekLabel(week) {
  if (week.label && week.label.trim()) return week.label
  if (week.week) return `${week.week}주차`
  return '구간'
}

// ── 구간 이미지 헬퍼 ──
function getPeriodImages(period) {
  if (period.images && period.images.length > 0) return period.images
  if (period.image) return [period.image]
  return []
}

// ── 핵심 수치 ──
const maxAmount = computed(() => {
  if (weeksList.value.length === 0) return props.policy?.amount || 0
  return Math.max(...weeksList.value.map(w => w.amount || 0))
})

const periodCount = computed(() => {
  return weeksList.value.length || 1
})

const calculateDays = computed(() => {
  if (!props.policy) return 0
  const start = new Date(props.policy.start_date)
  const end = new Date(props.policy.expire_date)
  return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1
})

// ── 상태 ──
const status = computed(() => {
  if (!props.policy) return 'unknown'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const start = new Date(props.policy.start_date)
  const end = new Date(props.policy.expire_date)
  if (today < start) return 'upcoming'
  if (today > end) return 'ended'
  return 'active'
})

const statusText = computed(() => {
  const map = { upcoming: '예정', active: '진행중', ended: '종료' }
  return map[status.value] || ''
})

const statusClass = computed(() => status.value)

// ── 단일 시책용 ──
const remainingDays = computed(() => {
  if (!props.policy || status.value !== 'active') return 0
  const today = new Date()
  const end = new Date(props.policy.expire_date)
  return Math.ceil((end - today) / (1000 * 60 * 60 * 24)) + 1
})

const progressPercent = computed(() => {
  if (!props.policy || status.value === 'upcoming') return 0
  if (status.value === 'ended') return 100
  const today = new Date()
  const start = new Date(props.policy.start_date)
  const end = new Date(props.policy.expire_date)
  return Math.min(100, Math.max(0, ((today - start) / (end - start)) * 100))
})

// ── 주차(구간) 상태 ──
function getWeekStatus(week) {
  if (!week?.start_date || !week?.end_date) return 'unknown'
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const start = new Date(week.start_date)
  const end = new Date(week.end_date)
  if (today < start) return 'upcoming'
  if (today > end) return 'ended'
  return 'active'
}

function getWeekProgress(week) {
  const s = getWeekStatus(week)
  if (s === 'upcoming') return 0
  if (s === 'ended') return 100
  const today = new Date()
  const start = new Date(week.start_date)
  const end = new Date(week.end_date)
  return Math.min(100, Math.max(0, ((today - start) / (end - start)) * 100))
}

function getWeekRemainingDays(week) {
  if (getWeekStatus(week) !== 'active') return 0
  const today = new Date()
  const end = new Date(week.end_date)
  return Math.ceil((end - today) / (1000 * 60 * 60 * 24)) + 1
}

function getWeekDaysUntilStart(week) {
  if (getWeekStatus(week) !== 'upcoming') return 0
  const today = new Date()
  const start = new Date(week.start_date)
  return Math.ceil((start - today) / (1000 * 60 * 60 * 24))
}

// ── 이미지 ──
function getFullUrl(url) {
  if (!url) return ''
  if (url.startsWith('http')) return url
  return window.location.origin + url
}

const extraImagesList = computed(() => {
  if (!props.policy?.extra_images) return []
  let images = []
  if (props.policy.extra_images.startsWith('[')) {
    try { images = JSON.parse(props.policy.extra_images) } catch { images = [] }
  } else {
    images = props.policy.extra_images.split(',').filter(url => url.trim())
  }
  return images.map(getFullUrl)
})

function openImageView(type, index = 0) {
  viewerImages.value = extraImagesList.value
  viewerStartIndex.value = index
  showImageView.value = true
}

function openPosterView() {
  viewerImages.value = [getFullUrl(props.policy.image), ...extraImagesList.value]
  viewerStartIndex.value = 0
  showImageView.value = true
}

function openPeriodImageView(weekIndex, imgIndex) {
  const week = weeksList.value[weekIndex]
  const images = getPeriodImages(week).map(getFullUrl)
  if (images.length === 0) return
  viewerImages.value = images
  viewerStartIndex.value = imgIndex
  showImageView.value = true
}

function closeImageView() {
  showImageView.value = false
}

// ── 포맷 ──
function formatNumber(num) {
  return num?.toLocaleString('ko-KR') || '0'
}

function formatDateShort(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return `${d.getMonth() + 1}/${d.getDate()}`
}

function formatDateRange(start, end) {
  if (!start || !end) return ''
  const s = new Date(start)
  const e = new Date(end)
  const fmt = d => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
  return `${fmt(s)} ~ ${fmt(e)}`
}

function formatDateTime(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`
}

// ── 이벤트 ──
function handleClose() { emit('close') }
function handleEdit() { emit('edit', props.policy) }
function handleDelete() { emit('delete', props.policy) }
</script>

<style scoped>
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(16px); }
  to { opacity: 1; transform: translateY(0); }
}

.dialog-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-index-top);
  padding: 16px;
  box-sizing: border-box;
  animation: fadeIn 0.2s ease;
}

.dialog-container {
  background: var(--color-background);
  border-radius: 16px;
  width: 100%;
  max-width: 440px;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.4);
  animation: slideUp 0.25s ease;
}

/* ── 헤더 ── */
.view-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid var(--color-surface-reverse);
  flex-shrink: 0;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.company-logo {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  object-fit: contain;
  background: var(--color-surface-light);
}

.company-logo.placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text-secondary);
}

.company-name {
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.header-right {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-pill {
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 20px;
}

.status-pill.active {
  background: rgba(16, 185, 129, 0.12);
  color: #10b981;
}

.status-pill.upcoming {
  background: rgba(59, 130, 246, 0.12);
  color: #3b82f6;
}

.status-pill.ended {
  background: var(--color-surface-reverse);
  color: var(--color-text-third);
}

.btn-close {
  width: 30px;
  height: 30px;
  border: none;
  background: var(--color-surface-reverse);
  border-radius: 8px;
  cursor: pointer;
  color: var(--color-text-secondary);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.btn-close:hover {
  background: var(--color-text-third);
  color: var(--color-text-primary);
}

/* ── 스크롤 바디 ── */
.view-body {
  flex: 1;
  overflow-y: auto;
  padding: 18px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* 포스터 이미지 */
.poster-image {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
}

.poster-image img {
  width: 100%;
  display: block;
  transition: transform 0.3s;
}

.poster-image:hover img {
  transform: scale(1.02);
}

.poster-zoom {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.poster-zoom i {
  font-size: 28px;
  color: #fff;
}

.poster-image:hover .poster-zoom {
  opacity: 1;
}

/* 제목 */
.title-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.policy-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text-primary);
  line-height: 1.3;
}

.date-range {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: var(--color-text-third);
}

.date-range i {
  font-size: 14px;
}

/* 설명 */
.description {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-text-secondary);
  white-space: pre-line;
}

/* 지표 바 */
.stats-bar {
  display: flex;
  align-items: center;
  padding: 14px 0;
  border-radius: 12px;
  background: var(--color-surface-light);
  border: 1px solid var(--color-surface-reverse);
}

.stat {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.stat-num {
  font-size: 22px;
  font-weight: 700;
  color: var(--color-text-primary);
  letter-spacing: -0.5px;
}

.stat-num small {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary);
  margin-left: 2px;
}

.stat-label {
  font-size: 11px;
  color: var(--color-text-third);
}

.stat-divider {
  width: 1px;
  height: 32px;
  background: var(--color-surface-reverse);
}

/* 섹션 라벨 */
.section-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-text-third);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.section-label .count {
  background: var(--color-surface-reverse);
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 11px;
}

/* ── 타임라인 ── */
.timeline {
  display: flex;
  flex-direction: column;
}

.timeline-item {
  display: flex;
  gap: 14px;
}

.timeline-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 14px;
  flex-shrink: 0;
  padding-top: 6px;
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--color-surface-reverse);
  border: 2px solid var(--color-background);
  box-shadow: 0 0 0 2px var(--color-surface-reverse);
  flex-shrink: 0;
  z-index: 1;
}

.timeline-item.active .dot {
  background: #10b981;
  box-shadow: 0 0 0 2px rgba(16, 185, 129, 0.3);
}

.timeline-item.upcoming .dot {
  background: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.timeline-item.ended .dot {
  background: var(--color-text-third);
  box-shadow: 0 0 0 2px var(--color-surface-reverse);
}

.line {
  flex: 1;
  width: 2px;
  background: var(--color-surface-reverse);
  margin: 4px 0;
}

.timeline-content {
  flex: 1;
  padding-bottom: 20px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.timeline-item:last-child .timeline-content {
  padding-bottom: 0;
}

/* 구간 헤드 */
.period-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.period-label {
  font-size: 15px;
  font-weight: 700;
  color: var(--color-text-primary);
}

.period-status {
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}

.period-status.active {
  color: #10b981;
}

.period-status.upcoming {
  color: #3b82f6;
}

.period-status.ended {
  color: var(--color-text-third);
}

.period-dates {
  font-size: 13px;
  color: var(--color-text-third);
}

/* 미니 프로그레스 */
.mini-progress {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mini-track {
  flex: 1;
  height: 4px;
  background: var(--color-surface-reverse);
  border-radius: 2px;
  overflow: hidden;
}

.mini-fill {
  height: 100%;
  background: #10b981;
  border-radius: 2px;
  transition: width 0.3s;
}

.mini-percent {
  font-size: 11px;
  font-weight: 600;
  color: #10b981;
  white-space: nowrap;
}

/* 금액 + 목표 */
.period-detail {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.period-amount {
  font-size: 20px;
  font-weight: 700;
  color: var(--color-brand);
  letter-spacing: -0.5px;
}

.period-goal {
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.5;
  white-space: pre-line;
}

/* 구간 이미지 (다중) */
.period-images {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.period-image {
  position: relative;
  width: calc(50% - 3px);
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
}

.period-images .period-image:only-child {
  width: 100%;
}

.period-image img {
  width: 100%;
  display: block;
  transition: transform 0.3s;
}

.period-image:hover img {
  transform: scale(1.02);
}

.image-zoom-sm {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s;
}

.image-zoom-sm i {
  font-size: 22px;
  color: #fff;
}

.period-image:hover .image-zoom-sm {
  opacity: 1;
}

/* ── 단일 시책 (주차 없을 때) ── */
.progress-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.progress-endpoints {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: var(--color-text-third);
}

.remaining {
  font-weight: 600;
  color: #10b981;
}

.progress-track {
  height: 6px;
  background: var(--color-surface-reverse);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #10b981, #34d399);
  border-radius: 3px;
  transition: width 0.3s;
}

.goal-block {
  padding: 14px;
  border-radius: 12px;
  background: var(--color-surface-light);
  border: 1px solid var(--color-surface-reverse);
  border-left: 3px solid var(--color-brand);
}

.goal-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: var(--color-brand);
  margin-bottom: 8px;
}

.goal-block p {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--color-text-primary);
  white-space: pre-line;
}

/* 상세 이미지 */
.extra-images {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 4px;
  border-radius: 10px;
  overflow: hidden;
}

.grid-image {
  aspect-ratio: 1;
  cursor: pointer;
  overflow: hidden;
}

.grid-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s;
}

.grid-image:hover img {
  transform: scale(1.08);
}

/* 메타 */
.meta {
  display: flex;
  gap: 14px;
  padding-top: 10px;
  border-top: 1px solid var(--color-surface-reverse);
}

.meta span {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: var(--color-text-third);
}

.meta span i {
  font-size: 13px;
}

/* ── 푸터 ── */
.view-footer {
  display: flex;
  justify-content: space-between;
  padding: 14px 18px;
  border-top: 1px solid var(--color-surface-reverse);
  background: var(--color-surface-light);
  flex-shrink: 0;
}

.btn-ghost-danger {
  padding: 10px 16px;
  border: none;
  background: transparent;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: var(--color-special-warning);
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s;
}

.btn-ghost-danger:hover {
  background: rgba(218, 47, 71, 0.08);
}

.btn-primary {
  padding: 10px 20px;
  border: none;
  background: var(--color-brand);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s;
  box-shadow: 0 4px 12px rgba(255, 140, 0, 0.2);
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(255, 140, 0, 0.3);
}

/* 반응형 */
@media (max-width: 480px) {
  .dialog-overlay {
    padding: 10px;
  }
  
  .policy-title {
    font-size: 18px;
  }
  
  .stat-num {
    font-size: 18px;
  }
  
  .period-amount {
    font-size: 18px;
  }
  
  .view-body {
    padding: 14px;
  }
}
</style>